import React from "react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "@mui/lab/LoadingButton";
import TextField from "@mui/material/TextField";
import { toast } from "react-toastify";
import { ContractorConfig, Reservation } from "@types";
import { formatTimeSlotRange } from "utils/timeSlot";
import CountdownTimer from "./CountdownTimer";
import useUpdateClientReservation from "./useUpdateClientReservation";

interface ReservationDetailsProps {
  reservation: Reservation;
  contractorConfig?: ContractorConfig | null;
  onExpire: () => void;
  onReservationUpdate?: (reservation: Reservation) => void;
  onSaveSuccess?: () => void;
  onCancelReservation?: () => Promise<void>;
  isCancellingReservation?: boolean;
}

const DELIVERY_TYPE_LABELS: Record<string, string> = {
  drop: "Dépose",
  install: "Installation",
  direct_drop: "Dépose directe",
  afterwork: "Afterwork",
};

const RESERVATION_SLOT_TYPE_LABELS: Record<string, string> = {
  normal: "Classic",
  classic: "Classic",
  afterwork: "Afterwork",
  overflow_j1: "J+1",
  j1_overflow: "J+1",
  next_day: "J+1",
};

const getDeliveryTypeLabel = (article: {
  drop_article: boolean;
  install_article: boolean;
  direct_drop_article: boolean;
}) => {
  if (article.install_article) return DELIVERY_TYPE_LABELS.install;
  if (article.direct_drop_article) return DELIVERY_TYPE_LABELS.direct_drop;
  return DELIVERY_TYPE_LABELS.drop;
};

const getDeliveryTypeClass = (article: {
  drop_article: boolean;
  install_article: boolean;
  direct_drop_article: boolean;
}) => {
  if (article.install_article) return "reservation-article__type--install";
  if (article.direct_drop_article)
    return "reservation-article__type--direct-drop";
  return "reservation-article__type--drop";
};

const STATUS_ICONS: Record<string, JSX.Element> = {
  active: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
  ),
  expiring: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  ),
  expired: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
    </svg>
  ),
  cancelled: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm4.59 13.17L15.17 16.59 12 13.41l-3.17 3.18-1.42-1.42L10.59 12 7.41 8.83l1.42-1.42L12 10.59l3.17-3.18 1.42 1.42L13.41 12l3.18 3.17z" />
    </svg>
  ),
  used: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
  ),
};

const STATUS_LABELS: Record<string, string> = {
  active: "Réservation active",
  expiring: "Expire bientôt",
  expired: "Expirée",
  cancelled: "Annulee",
  used: "Utilisée",
};

const ReservationDetails = ({
  reservation,
  contractorConfig,
  onExpire,
  onReservationUpdate,
  onSaveSuccess,
  onCancelReservation,
  isCancellingReservation = false,
}: ReservationDetailsProps) => {
  dayjs.locale("fr");
  const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState(false);
  const [cancelError, setCancelError] = React.useState<string | null>(null);
  const [floor, setFloor] = React.useState<string>(
    reservation.floor !== null && reservation.floor !== undefined
      ? String(reservation.floor)
      : "",
  );
  const [floorTouched, setFloorTouched] = React.useState(false);
  const { updateClientReservation, loading: isUpdatingReservation } =
    useUpdateClientReservation();
  const floorMode = contractorConfig?.reservation_floor_mode || "required";
  const showFloorField = floorMode !== "hidden";
  const isFloorRequired = floorMode === "required";
  const reservationDate = reservation.date;
  const reservationStart = reservation.start;
  const reservationStop = reservation.stop;
  const reservationHalfday = reservation.halfday;

  React.useEffect(() => {
    setFloor(
      reservation.floor !== null && reservation.floor !== undefined
        ? String(reservation.floor)
        : "",
    );
    setFloorTouched(false);
  }, [reservation.floor]);

  const formattedDate = React.useMemo(() => {
    if (!reservationDate) return "";
    return dayjs(reservationDate).format("dddd D MMMM YYYY");
  }, [reservationDate]);

  const formattedTimeRange = React.useMemo(() => {
    if (!reservationStart || !reservationStop) {
      if (reservationHalfday === "morning") return "Matin";
      if (reservationHalfday === "afternoon") return "Après-midi";
      return "";
    }

    return formatTimeSlotRange(
      {
        date: reservationDate,
        start: reservationStart,
        stop: reservationStop,
      },
      "H[h]mm",
    );
  }, [reservationDate, reservationStart, reservationStop, reservationHalfday]);

  const halfdayLabel = React.useMemo(() => {
    if (reservationHalfday === "morning") return "Matin";
    if (reservationHalfday === "afternoon") return "Après-midi";
    return "";
  }, [reservationHalfday]);

  const reservationSlotTypeLabel = React.useMemo(() => {
    const normalizedDeliveryType = reservation.delivery_type?.toLowerCase();

    if (normalizedDeliveryType) {
      return (
        RESERVATION_SLOT_TYPE_LABELS[normalizedDeliveryType] ||
        reservation.delivery_type
      );
    }

    if (reservation.is_next_day) {
      return "J+1";
    }

    return "";
  }, [reservation.delivery_type, reservation.is_next_day]);

  const shouldShowNextDayBadge =
    reservation.is_next_day && reservationSlotTypeLabel !== "J+1";

  const isReservationCancellable =
    !!onCancelReservation &&
    (reservation.status === "active" || reservation.status === "expiring");

  const normalizedFloor = floor.trim();
  const floorValid =
    !showFloorField ||
    (normalizedFloor === ""
      ? !isFloorRequired
      : /^-?\d+$/.test(normalizedFloor));
  const showFloorError =
    showFloorField &&
    ((isFloorRequired && normalizedFloor === "") ||
      (floorTouched && !floorValid));
  const normalizedCurrentFloor =
    !showFloorField || normalizedFloor === ""
      ? null
      : floorValid
      ? parseInt(normalizedFloor, 10)
      : null;
  const hasFloorChanged =
    showFloorField && normalizedCurrentFloor !== reservation.floor;

  const handleFloorSave = async () => {
    setFloorTouched(true);

    if (!showFloorField || !floorValid) {
      return;
    }

    try {
      const updatedReservation = await updateClientReservation(
        normalizedCurrentFloor,
      );
      onReservationUpdate?.(updatedReservation);
      toast.success("Vos informations de livraison ont bien été enregistrées");
      onSaveSuccess?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Une erreur est survenue",
      );
    }
  };

  const handleCancelConfirm = async () => {
    if (!onCancelReservation) {
      return;
    }

    setCancelError(null);

    try {
      await onCancelReservation();
      setIsCancelDialogOpen(false);
    } catch (error) {
      setCancelError(
        error instanceof Error ? error.message : "Une erreur est survenue",
      );
    }
  };

  return (
    <>
      <div className="reservation-details">
        {/* Timer */}
        {(reservation.status === "active" ||
          reservation.status === "expiring") && (
          <div className="reservation-details__timer-section">
            <CountdownTimer
              expiresAt={reservation.expires_at}
              onExpire={onExpire}
            />
          </div>
        )}

        {/* Status badge */}
        <div
          className={`reservation-details__status reservation-details__status--${reservation.status}`}
        >
          {STATUS_ICONS[reservation.status]}
          {STATUS_LABELS[reservation.status] || reservation.status}
        </div>

        {/* Date & Time card */}
        <div className="reservation-details__card reservation-details__card--date">
          <div className="reservation-details__card-header">
            <svg
              className="reservation-details__icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
            >
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
            </svg>
            <span>Date et heure</span>
          </div>
          <div className="reservation-details__card-body">
            <div className="reservation-details__date">{formattedDate}</div>
            <div className="reservation-details__time-row">
              {halfdayLabel && (
                <span className="reservation-details__halfday">
                  {halfdayLabel}
                </span>
              )}
              {formattedTimeRange && (
                <span className="reservation-details__time-range">
                  {formattedTimeRange}
                </span>
              )}
            </div>
            <div className="reservation-details__badges">
              {reservationSlotTypeLabel && (
                <span className="reservation-details__badge reservation-details__badge--type">
                  {reservationSlotTypeLabel}
                </span>
              )}
              {shouldShowNextDayBadge && (
                <span className="reservation-details__badge reservation-details__badge--next-day">
                  J+1
                </span>
              )}
            </div>
          </div>
        </div>

        {showFloorField && (
          <div className="reservation-details__card reservation-details__card--contact">
            <div className="reservation-details__card-header">
              <svg
                className="reservation-details__icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M7 2h10a2 2 0 012 2v16a2 2 0 01-2 2H7a2 2 0 01-2-2V4a2 2 0 012-2zm5 19a1.25 1.25 0 100-2.5A1.25 1.25 0 0012 21zm-3-4h6V5H9v12z" />
              </svg>
              <span>Informations de livraison</span>
            </div>
            <div className="reservation-details__card-body reservation-details__card-body--contact">
              <div className="reservation-details__field-row">
                <TextField
                  required={isFloorRequired}
                  fullWidth
                  label={isFloorRequired ? "Étage" : "Étage (optionnel)"}
                  variant="outlined"
                  name="floor"
                  type="number"
                  value={floor}
                  onChange={(event) => {
                    setFloor(event.target.value);
                    if (!floorTouched) {
                      setFloorTouched(true);
                    }
                  }}
                  onBlur={() => setFloorTouched(true)}
                  error={showFloorError}
                  helperText={
                    showFloorError
                      ? isFloorRequired && normalizedFloor === ""
                        ? "L'étage est obligatoire pour cette réservation"
                        : isFloorRequired
                        ? "Veuillez saisir un étage valide"
                        : "Veuillez saisir un étage valide ou laisser vide"
                      : " "
                  }
                  disabled={isUpdatingReservation}
                />
              </div>
              <div className="reservation-details__action-row reservation-details__action-row--between">
                <p className="reservation-details__action-copy">
                  {isFloorRequired
                    ? "Modifiez l'étage si les informations de livraison ont changé."
                    : "Modifiez l'étage si les informations de livraison ont changé, ou laissez ce champ vide si vous ne le connaissez pas."}
                </p>
                <LoadingButton
                  variant="contained"
                  onClick={handleFloorSave}
                  loading={isUpdatingReservation}
                  disabled={!hasFloorChanged || !floorValid}
                >
                  Enregistrer
                </LoadingButton>
              </div>
            </div>
          </div>
        )}

        {/* Articles card */}
        {reservation.articles && reservation.articles.length > 0 && (
          <div className="reservation-details__card reservation-details__card--articles">
            <div className="reservation-details__card-header">
              <svg
                className="reservation-details__icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm6 16H6V8h2v2c0 .55.45 1 1 1s1-.45 1-1V8h4v2c0 .55.45 1 1 1s1-.45 1-1V8h2v12z" />
              </svg>
              <span>Articles ({reservation.articles.length})</span>
            </div>
            <div className="reservation-details__articles">
              {reservation.articles.map((article) => (
                <div key={article.id} className="reservation-article">
                  <span className="reservation-article__quantity">
                    ×{article.quantity}
                  </span>
                  <div className="reservation-article__info">
                    <span className="reservation-article__name">
                      {article.article_name || "Article"}
                    </span>
                  </div>
                  <span
                    className={`reservation-article__type ${getDeliveryTypeClass(
                      article,
                    )}`}
                  >
                    {getDeliveryTypeLabel(article)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {isReservationCancellable && (
          <div className="reservation-details__card reservation-details__card--actions">
            <div className="reservation-details__card-header">
              <svg
                className="reservation-details__icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm4.59 13.17L15.17 16.59 12 13.41l-3.17 3.18-1.42-1.42L10.59 12 7.41 8.83l1.42-1.42L12 10.59l3.17-3.18 1.42 1.42L13.41 12l3.18 3.17z" />
              </svg>
              <span>Annulation</span>
            </div>
            <div className="reservation-details__card-body reservation-details__card-body--actions">
              <p className="reservation-details__action-copy">
                Si vous n&apos;avez plus besoin de ce creneau, vous pouvez le
                liberer immediatement.
              </p>
              <div className="reservation-details__action-row">
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    setCancelError(null);
                    setIsCancelDialogOpen(true);
                  }}
                  disabled={isCancellingReservation}
                >
                  Annuler la reservation
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={isCancelDialogOpen}
        onClose={
          isCancellingReservation
            ? undefined
            : () => setIsCancelDialogOpen(false)
        }
      >
        <DialogTitle>Annuler cette reservation ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Le creneau du {formattedDate}{" "}
            {halfdayLabel ? `(${halfdayLabel}) ` : ""}
            sera libere immediatement et pourra etre reserve par un autre
            client.
          </DialogContentText>
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsCancelDialogOpen(false)}
            disabled={isCancellingReservation}
          >
            Garder la reservation
          </Button>
          <Button
            color="error"
            onClick={handleCancelConfirm}
            disabled={isCancellingReservation}
            variant="contained"
          >
            {isCancellingReservation ? "Annulation..." : "Confirmer"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReservationDetails;
